import { ClipboardEvent, useContext, useEffect, useState } from 'react'

import {
  Box,
  Button,
  DragNDrop,
  Icon,
  Input,
  Modal,
  Text,
  TextArea,
} from '@fairfashion/design-system'
import { useForm } from 'react-hook-form'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

import { ConfirmModalContext } from '../../../../components/ConfirmModal'
import { FeedbackAlertContext } from '../../../../components/FeedbackAlert'
import { QRCodeDetailsContext } from '../../qrcode-details.context'

import * as Styles from './edit-item-traceability-step-modal.styles'
import {
  EditItemTraceabilityStepForm,
  EditItemTraceabilityStepModalProps,
} from './edit-item-traceability-step-modal.types'

export const EditItemTraceabilityStepModal = ({
  open,
  onClose,
  itemTraceabilityStep,
}: EditItemTraceabilityStepModalProps) => {
  const [videoInput, setVideoInput] = useState('')
  const [images, setImages] = useState<File[]>([])
  const [saving, setSaving] = useState(false)

  const [defaultImages, setDefaultImages] = useState<string[]>([])

  const { confirmModal } = useContext(ConfirmModalContext)
  const { feedbackAlert } = useContext(FeedbackAlertContext)
  const { actions } = useContext(QRCodeDetailsContext)

  const { handleSubmit, watch, register, reset, setValue } =
    useForm<EditItemTraceabilityStepForm>({
      values: {
        name: '',
        description: '',
        shortDescription: '',
        images: [],
        videos: [],
      },
    })

  useEffect(() => {
    if (open && itemTraceabilityStep) {
      reset({
        name: itemTraceabilityStep?.name ?? '',
        description: itemTraceabilityStep?.description ?? '',
        shortDescription: itemTraceabilityStep?.shortDescription ?? '',
        images: itemTraceabilityStep?.assets?.images ?? [],
        videos: itemTraceabilityStep?.assets?.videos ?? [],
      })

      setDefaultImages(itemTraceabilityStep?.assets?.images ?? [])
      setImages([])
      setVideoInput('')
    } else {
      reset({
        name: '',
        description: '',
        images: [],
        videos: [],
      })
    }
  }, [open])

  const onSubmit = async (data: any) => {
    if (!itemTraceabilityStep) return

    setSaving(true)
    const imagesUrls: string[] = [...(defaultImages || [])]

    if (images?.length) {
      for (const image of images) {
        const url = await actions.createItemTraceabilityStepImage(
          itemTraceabilityStep.title,
          image,
        )

        if (url) {
          imagesUrls.push(url)
        }
      }
    }

    await actions.saveItemTraceabilityStep(itemTraceabilityStep.title, {
      ...data,
      images: imagesUrls,
    })

    setSaving(false)
    onClose()
    feedbackAlert('Etapa alterada com sucesso!', 'success')
  }

  const handlePasteVideoUrl = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pattern1 = /(?:\?|&)v=([a-zA-Z0-9_-]+)/
    const pattern2 = /youtu\.be\/([a-zA-Z0-9_-]+)/
    const text = e.clipboardData.getData('text/plain')
    const match1 = text.match(pattern1)
    const match2 = text.match(pattern2)

    if (match1) {
      setVideoInput(match1[1])
      return
    }
    if (match2) {
      setVideoInput(match2[1])
      return
    }

    setVideoInput(text)
  }

  const handleAddVideo = () => {
    if (videoInput) {
      const prefix = 'https://www.youtube.com/watch?v='
      const videoAlreadyExists = videos?.find((v) => v === prefix + videoInput)

      if (!videoAlreadyExists) {
        setValue('videos', [...videos, prefix + videoInput])
      }

      setVideoInput('')
    }
  }

  const handleDeleteVideo = async (index: number) => {
    if (
      await confirmModal(
        'Excluir vídeo',
        'Tem certeza que deseja excluir este vídeo?',
      )
    ) {
      const newVideos = videos?.filter((video, i) => i !== index)
      setValue('videos', newVideos)
    }
  }

  const videos = watch('videos')

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Etapa"
      width="800px"
      maxWidth="90%"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box width="100%" direction="column" gap={3} padding={1}>
          <Box width="100%" direction="column" gap={2}>
            <Input label="Nome da Etapa" {...register('name')} required />
            <TextArea
              label="Descrição curta"
              rows={2}
              {...register('shortDescription')}
              required
            />
            <Box direction="column" gap={1}>
              <Text size="small" color="grey_1">
                Descrição da Etapa
              </Text>
              <ReactQuill
                theme="snow"
                value={watch('description')}
                onChange={(content: string) => {
                  setValue('description', content)
                }}
                modules={{
                  toolbar: [
                    ['bold', 'italic', 'underline'],
                    [{ list: 'ordered' }, { list: 'bullet' }],
                    ['clean'],
                  ],
                }}
                style={{ height: '200px', marginBottom: '50px' }}
              />
            </Box>
          </Box>
          <Box width="100%" direction="column" gap={2}>
            <Text>Vídeos</Text>
            <Input
              value={videoInput}
              onChange={(e) => setVideoInput(e.target.value)}
              label="URL do vídeo no YouTube"
              placeholder="ID do vídeo"
              adornment={
                <Text color="grey_3" size="small">
                  https://www.youtube.com/watch?v=
                </Text>
              }
              adornmentPosition="left"
              onPaste={handlePasteVideoUrl}
            />
            <Button
              variant="outlined"
              onClick={handleAddVideo}
              padding="10px 12px"
            >
              <Icon name="add" color="grey_1" />
              <Text fontWeight={500} size="small">
                Adicionar URL
              </Text>
            </Button>
            <Box gap={1} flexWrap="wrap">
              {videos?.map((video, index) => (
                <Styles.VideoTag key={`video-${index}`} gap={1}>
                  <Styles.VideoText size="small">
                    <a href={video} target="_blank">
                      {video}
                    </a>
                  </Styles.VideoText>

                  <Button
                    variant="text"
                    padding="0"
                    onClick={() => handleDeleteVideo(index)}
                  >
                    <Icon name="close" color="grey_1" />
                  </Button>
                </Styles.VideoTag>
              ))}
            </Box>
          </Box>
          <Box width="100%" direction="column" gap={2}>
            <Text>Imagens</Text>

            {open && (
              <DragNDrop
                onChange={(files) => files && setImages(files)}
                defaultFiles={defaultImages || []}
                onDeleteDefaultFile={(index: number) => {
                  const newDefaultImages = [...defaultImages]
                  newDefaultImages.splice(index, 1)
                  setDefaultImages(newDefaultImages)
                }}
                preview="image"
                fileType=".png, .jpg, .jpeg, .webp, image/png, image/jpeg, image/webp"
                fileTypeError="Tipo de arquivo inválido"
                maxSize={5 * 1024 * 1024}
                maxSizeError="Arquivo muito grande. O tamanho máximo para arquivo é 5MB."
                multiple
              />
            )}
          </Box>

          <Box width="100%" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={onClose} padding="10px 16px">
              <Text size="small" fontWeight={500}>
                Cancelar
              </Text>
            </Button>
            <Button padding="10px 16px" type="submit" loading={saving}>
              <Text size="small" fontWeight={500} color="white">
                Salvar
              </Text>
            </Button>
          </Box>
        </Box>
      </form>
    </Modal>
  )
}
